/**
 * Duplicate a matrix, applying adjustments on the way out.
 *
 * Thibault's ask was "the same floors next winter, a bit dearer". The
 * mock called it *Range replica* and fused two things: copying a grid
 * with a transformation, and hanging the copy off a date range. The
 * second half already exists — a binding row duplicates with new dates
 * in Price limits. So this dialog does only the first, and what comes
 * out is **an ordinary matrix**: live, editable, and dragging its own
 * bindings like any other. No frozen snapshot, no dates, no new tab
 * (CU-869f9n466 phase 2; the reasoning is in the UX gate).
 *
 * It does not save. This tab never POSTs: `useSetupCollection` diffuses
 * the whole collection against a snapshot with a 600 ms debounce, so a
 * direct create here would leave the hook's `serverRef` behind and the
 * next sync would create the matrix a second time. The dialog hands the
 * copy to the list and closes; the tab shows the save, and a failure
 * arrives as the toast the hook already raises.
 */
import React, { useMemo, useState } from 'react'
import {
	Autocomplete, Box, Button, Chip, IconButton, MenuItem, Select, Stack, Table, TableBody, TableCell,
	TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import { dsColorNew } from '@palette'
import {
	AppDialog, AppDialogHeader, AppDialogBody, AppDialogFooter, AppDialogCancel, AppNote, AppBulkEditAction, AppPillToggle,
} from '@components'
import { dialogBodySx } from 'Pages/DsRepricing/@components/dialogBodySx'
import { setupScopeSx } from 'Pages/DsRepricing/Setup/@components/setupScopeSx'
import { Add as AddIcon, CloseOutlined as CloseIcon, ArrowRightAltOutlined as ArrowIcon } from 'Pages/DsRepricing/@components/icons'
import { inputSx } from 'Pages/DsRepricing/Setup/@components/formStyles'
import { headCellSx } from 'Pages/DsRepricing/Setup/@components/formStyles'
import {
	applyAdjustments, codesOf, copyName, previewRows,
} from 'Pages/DsRepricing/Setup/@services/matrixAdjust'

const T = { body: 14, label: 13, caption: 12 }
const W = { heavy: 700, bold: 600, medium: 500 }

const money = (n) => (n == null ? null
	: new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2,
	                                   maximumFractionDigits: 2 }).format(n))

const bandLabel = (b) => (b.to == null ? `${b.from}+` : `${b.from}–${b.to}`)

/** An adjustment is one of two kinds, each with its own name. A single
 *  "all / some" selector would be a trap: with one car written in euros
 *  and another in percent, "+5 % to all" moves them by different
 *  percentages, for the same words (TL, 2026-10-03). */
const KINDS = {
	reference: {
		short: 'The reference price',
		hint: 'Every ACRISS follows through its own adjustment. A car written '
		    + 'in €/day keeps its amount, so the gap between categories stays in euros.',
	},
	cars: {
		short: 'Only chosen cars',
		hint: 'Each chosen car ends exactly this much dearer or cheaper.',
	},
}

let seq = 0
const blankAdjustment = () => ({
	id: `adj-${++seq}`, kind: 'reference', acriss: [], sign: 1, amount: '', unit: 'percent',
})

export default function DuplicateDialog({ matrix, existingNames, onCreate, onClose }) {
	const [name, setName] = useState(() => copyName(matrix.name, existingNames))
	const [adjustments, setAdjustments] = useState([])

	const { grid, warnings, errors } = useMemo(
		() => applyAdjustments(matrix, adjustments), [matrix, adjustments])
	const copy = useMemo(() => ({ ...matrix, ...grid }), [matrix, grid])
	const rows = useMemo(() => previewRows(matrix, copy), [matrix, copy])
	const codes = codesOf(matrix)

	// At least as lax as the server's, which compares on a case-insensitive
	// collation — a `===` here would let the clash through to a toast
	// half a second after the dialog closed.
	const clash = existingNames.some(n => String(n || '').trim().toLowerCase()
		=== name.trim().toLowerCase())
	const nameError = !name.trim() ? 'The copy needs a name.'
		: name.length > 120 ? 'At most 120 characters.'
		: clash ? 'There is already a matrix with this name.' : null

	const patch = (id, changes) => setAdjustments(all => all.map(
		a => (a.id === id ? { ...a, ...changes } : a)))

	// One path for any number of adjustments, zero included. With zero this
	// is, by construction, the same copy `exactCopy` builds for the ⧉ on the
	// row — the test in `matrixAdjust.test.mjs` fixes that identity, so no
	// branch here (TL C2, 2026-10-08).
	const create = () => {
		onCreate({ ...copy, name: name.trim() })
		onClose()
	}

	return (
		<AppDialog open onClose={onClose} maxWidth="md" paperSx={setupScopeSx}>
			<AppDialogHeader icon="clone" title="Duplicate with adjustments" onClose={onClose}
			                 subtitle={<>
				                 A copy of <strong>{matrix.name || 'Untitled'}</strong>. It is a matrix like
				                 any other from the moment you create it — attach it to a scope and a date
				                 range over in Price limits.
			                 </>}/>
			<AppDialogBody sx={dialogBodySx}>
				<TextField label="Name of the copy" value={name} fullWidth size="small"
				           onChange={(e) => setName(e.target.value)}
				           error={!!nameError} helperText={nameError || ' '}
				           inputProps={{ maxLength: 120 }}
				           sx={{ ...inputSx, mb: 2 }}/>

				<Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
					<Typography sx={{ fontWeight: W.heavy, fontSize: T.label,
					                  textTransform: 'uppercase', letterSpacing: '0.05em', flex: 1 }}>
						Adjustments applied to the copy
					</Typography>
					<Button size="small" startIcon={<AddIcon/>}
					        onClick={() => setAdjustments(all => [...all, blankAdjustment()])}>
						Add adjustment
					</Button>
				</Box>

				{adjustments.length === 0 ? (
					<Box sx={{ mb: 2 }}>
						<Typography sx={{ fontSize: T.label, color: dsColorNew.textMuted }}>
							Exact copy — same floors as {matrix.name || 'the original'}. It changes on
							its own from the moment you create it.
						</Typography>
						{/* The path Thibault did not find: a copy with nothing to
						    adjust is the main case, not the degenerate one. A button,
						    not a link — it creates data. Disabled only by the name:
						    with zero adjustments `errors` can only come from the
						    original itself, and the ⧉ on the row would copy it
						    anyway (TL, 2026-10-08). */}
						<Typography sx={{ fontSize: T.label, color: dsColorNew.textMuted, mt: 0.75 }}>
							Nothing to adjust?{' '}
							<Button size="small" variant="text" onClick={create} disabled={!!nameError}
							        sx={{ fontWeight: W.bold, p: 0, minWidth: 0,
							              verticalAlign: 'baseline', fontSize: 'inherit' }}>
								Duplicate to edit
							</Button>
							{' '}creates it and opens it now — the ⧉ icon on the row does the same in one click.
						</Typography>
					</Box>
				) : (
					<Stack gap={1.25} sx={{ mb: 2 }}>
						{adjustments.map((a, i) => (
							<AdjustmentRow key={a.id} index={i + 1} adjustment={a} codes={codes}
							               baseAcriss={matrix.base_acriss}
							               onChange={(c) => patch(a.id, c)}
							               onRemove={() => setAdjustments(all => all.filter(x => x.id !== a.id))}/>
						))}
						<Typography sx={{ fontSize: T.caption, color: dsColorNew.textMuted }}>
							Applied in order, each one on the result of the last.
						</Typography>
					</Stack>
				)}

				{errors.map((e, i) => (
					<AppNote key={i} severity="error" sx={{ mb: 1 }}>{e}</AppNote>
				))}
				{warnings.map((w, i) => (
					<AppNote key={i} severity="warning" sx={{ mb: 1 }}>{w}</AppNote>
				))}

				<Typography sx={{ fontWeight: W.heavy, fontSize: T.label, textTransform: 'uppercase',
				                  letterSpacing: '0.05em', mt: 2, mb: 1 }}>
					Before → after
				</Typography>
				<Preview rows={rows} codes={codes} baseAcriss={matrix.base_acriss}/>
			</AppDialogBody>
			<AppDialogFooter>
				<AppDialogCancel onClick={onClose}/>
				<AppBulkEditAction onClick={create} disabled={!!nameError || errors.length > 0}>
					Create copy
				</AppBulkEditAction>
			</AppDialogFooter>
		</AppDialog>
	)
}

// Exported for the render smoke test: the dialog itself renders into a
// portal, which server-side rendering does not execute, so these two
// would never run there and a mount error would ship unseen — which is
// exactly how the Price limits crash of 2026-10-03 got out.
export function AdjustmentRow({ index, adjustment, codes, baseAcriss, onChange, onRemove }) {
	const a = adjustment
	// The reference ACRISS is not selectable: applying an adjustment to it
	// would move `base`, which is a reference adjustment wearing a
	// disguise — and giving it a rule of its own is refused by the server.
	const choosable = codes.filter(c => c !== baseAcriss)
	return (
		<Box sx={{ border: '1px solid', borderColor: dsColorNew.grayBorder, borderRadius: '10px', p: 1.25 }}>
			<Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
				<Typography sx={{ fontSize: T.caption, color: dsColorNew.textMuted, fontWeight: W.bold,
				                  minWidth: 18 }}>
					{index}
				</Typography>
				{/* Both kinds on screen at once. Behind a select, the per-car
				    mode was invisible — you had to open a dropdown already
				    showing the other option to find out it existed (Curro,
				    2026-10-03: "el adjustment no puede filtrar acriss, no?"). */}
				<Typography sx={{ fontSize: T.caption, color: dsColorNew.textMuted }}>Move</Typography>
				<AppPillToggle size="small" value={a.kind}
				               options={Object.entries(KINDS).map(([k, v]) => ({ value: k, label: v.short }))}
				               onChange={(v) => onChange({ kind: v })}/>
				{/* The row reads as one sentence — "move only chosen cars by
				    +5 %". Without the "by", the toggle looked like a filter and
				    the amount beside it looked like a second, unrelated control
				    (Curro, 2026-10-03: "no veo claro"). */}
				<Typography sx={{ fontSize: T.caption, color: dsColorNew.textMuted }}>by</Typography>
				<AppPillToggle size="small" value={a.sign}
				               options={[{ value: 1, label: '+' }, { value: -1, label: '−' }]}
				               onChange={(v) => onChange({ sign: v })}/>
				<TextField size="small" type="number" value={a.amount} placeholder="0"
				           onChange={(e) => onChange({ amount: e.target.value })}
				           inputProps={{ min: 0, step: 0.01, style: { width: 72, textAlign: 'right', fontWeight: W.bold } }}
				           sx={{ ...inputSx, '& .MuiOutlinedInput-root': { height: 38 } }}/>
				<Select size="small" value={a.unit} sx={{ ...inputSx, minWidth: 88, height: 38 }}
				        onChange={(e) => onChange({ unit: e.target.value })}>
					<MenuItem value="percent">%</MenuItem>
					<MenuItem value="euro">€/day</MenuItem>
				</Select>
				<Box flex={1}/>
				<IconButton size="small" onClick={onRemove} aria-label="Remove this adjustment">
					<CloseIcon fontSize="small"/>
				</IconButton>
			</Box>
			{/* Directly under the sentence it completes: "…only chosen cars"
			    is unfinished until these are picked. */}
			{a.kind === 'cars' && (
				<Autocomplete multiple size="small" options={choosable} value={a.acriss}
				              onChange={(_, v) => onChange({ acriss: v })}
				              sx={{ mt: 1 }}
				              renderInput={(p) => <TextField {...p} autoFocus={a.acriss.length === 0}
				                                             placeholder="Which cars? Pick one or more…" sx={inputSx}/>}
				              renderTags={(value, getProps) => value.map((v, i) => (
					              <Chip size="small" label={v} {...getProps({ index: i })} key={v}/>
				              ))}/>
			)}
			<Typography sx={{ fontSize: T.caption, color: dsColorNew.textMuted, mt: 0.75 }}>
				{KINDS[a.kind].hint}
				{a.kind === 'cars' && ` ${baseAcriss} is the reference and is not on the list — `
					+ 'to move it, choose "The reference price".'}
			</Typography>
		</Box>
	)
}

export function Preview({ rows, codes, baseAcriss }) {
	return (
		<TableContainer sx={{ border: '1px solid', borderColor: dsColorNew.grayBorder, borderRadius: 1,
		                      overflow: 'auto', maxHeight: 320 }}>
			<Table size="small" stickyHeader>
				<TableHead>
					<TableRow sx={{ '& th': { ...headCellSx, fontSize: T.caption } }}>
						{/* Sticky, like the editor's: scrolling right must not lose
						    which band each row is. */}
						<TableCell sx={{ position: 'sticky', left: 0, zIndex: 3,
						                 bgcolor: dsColorNew.tableHead, borderRight: '1px solid',
						                 borderRightColor: dsColorNew.grayBorder }}>Days</TableCell>
						{codes.map(c => (
							<TableCell key={c} align="right">
								{c}{c === baseAcriss ? ' ·ref' : ''}
							</TableCell>
						))}
					</TableRow>
				</TableHead>
				<TableBody>
					{rows.map(r => (
						<TableRow key={`${r.band.from}-${r.band.to}`} hover>
							<TableCell sx={{ fontWeight: W.bold, fontSize: T.label, whiteSpace: 'nowrap',
							                 position: 'sticky', left: 0, zIndex: 2, bgcolor: dsColorNew.white,
							                 borderRight: '1px solid', borderRightColor: dsColorNew.grayBorder }}>
								{bandLabel(r.band)}
							</TableCell>
							{codes.map(c => <Cell key={c} cell={r.cells[c]}/>)}
						</TableRow>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	)
}

function Cell({ cell }) {
	const { before, after } = cell || {}
	if (before == null && after == null) {
		return (
			<TableCell align="right" sx={{ color: dsColorNew.textSubtle, fontSize: T.caption, fontStyle: 'italic' }}>
				no floor
			</TableCell>
		)
	}
	const moved = before !== after
	return (
		<TableCell align="right" sx={{ whiteSpace: 'nowrap', fontFamily: 'inherit' }}>
			<Box component="span" sx={{ color: dsColorNew.textMuted, fontSize: T.caption }}>
				{money(before) ?? '—'}
			</Box>
			{moved && (<>
				<ArrowIcon sx={{ fontSize: 14, mx: 0.25, verticalAlign: 'middle', color: dsColorNew.textSubtle }}/>
				<Box component="span" sx={{ fontWeight: W.heavy, fontSize: T.label,
				                            color: after == null ? dsColorNew.textSubtle
				                                 : after > before ? dsColorNew.successText : dsColorNew.dangerRed }}>
					{money(after) ?? 'no floor'}
				</Box>
			</>)}
		</TableCell>
	)
}
